/**
 * 废土地形烘焙。
 *
 * 9216 格一次性画进离屏 Canvas：噪声着色的地面、体积岩石、矿晶、
 * 裂隙与废墟道具。运行期 GPU 只画一个四边形。
 */

import { MAP_W, MAP_H } from '../../shared/constants.ts';
import { Tile } from '../../sim/map/terrain.ts';
import { HALF_H, HALF_W } from '../iso.ts';
import { rasterize } from '../mesh3d/raster.ts';
import { buildPropMesh } from '../mesh3d/models.ts';

export interface TerrainBake {
  canvas: HTMLCanvasElement;
  minimap: HTMLCanvasElement;
  originX: number;
  scale: number;
}

function hash(x: number, y: number): number {
  let n = Math.imul(x | 0, 374761393) + Math.imul(y | 0, 668265263);
  n = Math.imul(n ^ (n >>> 13), 1274126177);
  return ((n ^ (n >>> 16)) >>> 0) / 4294967295;
}

function vnoise(x: number, y: number): number {
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const fx = x - x0;
  const fy = y - y0;
  const u = fx * fx * (3 - 2 * fx);
  const v = fy * fy * (3 - 2 * fy);
  const a = hash(x0, y0);
  const b = hash(x0 + 1, y0);
  const c = hash(x0, y0 + 1);
  const d = hash(x0 + 1, y0 + 1);
  return a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v;
}

function fbm(x: number, y: number): number {
  return vnoise(x, y) * 0.55 + vnoise(x * 2.1, y * 2.1) * 0.3 + vnoise(x * 4.3, y * 4.3) * 0.15;
}

function nearBase(tx: number, ty: number, bases: Array<{ x: number; y: number }>, r: number): boolean {
  for (const b of bases) {
    const dx = b.x - tx;
    const dy = b.y - ty;
    if (dx * dx + dy * dy < r * r) return true;
  }
  return false;
}

function diamond(ctx: CanvasRenderingContext2D, px: number, py: number, bw: number, bh: number): void {
  ctx.beginPath();
  ctx.moveTo(px, py);
  ctx.lineTo(px + bw, py + bh);
  ctx.lineTo(px, py + bh * 2);
  ctx.lineTo(px - bw, py + bh);
  ctx.closePath();
}

interface Stamp {
  canvas: HTMLCanvasElement;
  gx: number;
  gy: number;
}

let stampCache: Record<string, Stamp[]> | null = null;

function makeStamps(): Record<string, Stamp[]> {
  if (stampCache) return stampCache;
  const out: Record<string, Stamp[]> = { rock: [], ore: [], scrap: [], barrel: [], ruin: [], spire: [] };
  const kinds = ['rock', 'ore', 'scrap', 'barrel', 'ruin', 'spire'] as const;
  const counts = { rock: 8, ore: 5, scrap: 5, barrel: 3, ruin: 4, spire: 3 };
  const scales = { rock: 17, ore: 15, scrap: 13, barrel: 14, ruin: 15, spire: 16 };
  for (const k of kinds) {
    for (let i = 0; i < counts[k]; i++) {
      const mesh = buildPropMesh(k, 1000 + i * 97);
      const r = rasterize(mesh, { scale: scales[k], grain: 0.1, shadow: k !== 'spire' });
      out[k]!.push({ canvas: r.canvas, gx: r.groundX, gy: r.groundY });
    }
  }
  stampCache = out;
  return out;
}

export function prefetchTerrainStamps(): void {
  makeStamps();
}

export function bakeTerrain(
  tiles: Uint8Array,
  rifts: Array<{ x: number; y: number }>,
  bases: Array<{ x: number; y: number }> = [],
): TerrainBake {
  const S = 0.5;
  const bw = HALF_W * S;
  const bh = HALF_H * S;
  const OX = MAP_H * bw;
  const W = (MAP_W + MAP_H) * bw;
  const H = (MAP_W + MAP_H) * bh + bh * 8;

  const c = document.createElement('canvas');
  c.width = Math.ceil(W);
  c.height = Math.ceil(H);
  const ctx = c.getContext('2d')!;

  const stamps = makeStamps();

  const rockAdj = new Uint8Array(MAP_W * MAP_H);
  for (let ty = 0; ty < MAP_H; ty++) {
    for (let tx = 0; tx < MAP_W; tx++) {
      if (tiles[ty * MAP_W + tx] !== Tile.Rock) continue;
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          const nx = tx + dx;
          const ny = ty + dy;
          if (nx < 0 || ny < 0 || nx >= MAP_W || ny >= MAP_H) continue;
          rockAdj[ny * MAP_W + nx]!++;
        }
      }
    }
  }

  // 先铺全部地面菱形（含矿、裂隙底色）
  for (let sum = 0; sum < MAP_W + MAP_H; sum++) {
    for (let tx = 0; tx < MAP_W; tx++) {
      const ty = sum - tx;
      if (ty < 0 || ty >= MAP_H) continue;
      const t = tiles[ty * MAP_W + tx]!;
      const px = (tx - ty) * bw + OX;
      const py = (tx + ty) * bh;
      const n = fbm(tx * 0.17, ty * 0.17);
      const n2 = hash(tx, ty);

      let r: number;
      let g: number;
      let b: number;
      if (t === Tile.Rock) {
        r = 86 + n * 22;
        g = 80 + n * 16;
        b = 70 + n * 12;
      } else if (t === Tile.Ore) {
        r = 118 + n * 40;
        g = 92 + n * 22;
        b = 42 + n * 10;
      } else if (t === Tile.Rift) {
        r = 42 + n * 18;
        g = 32 + n * 12;
        b = 62 + n * 28;
      } else {
        r = 62 + n * 28;
        g = 68 + n * 22;
        b = 52 + n * 14;
        // 棋盘微差
        if ((tx + ty) & 1) {
          r -= 4;
          g -= 3;
          b -= 2;
        }
      }

      const ao = Math.min(1, rockAdj[ty * MAP_W + tx]! / 6);
      r *= 1 - ao * 0.28;
      g *= 1 - ao * 0.28;
      b *= 1 - ao * 0.32;

      ctx.fillStyle = `rgb(${r | 0},${g | 0},${b | 0})`;
      diamond(ctx, px, py, bw, bh);
      ctx.fill();

      // 地面裂纹 / 砾石点
      if (t === Tile.Ground && n2 > 0.62) {
        ctx.strokeStyle = `rgba(20,16,12,${0.12 + n * 0.12})`;
        ctx.lineWidth = 0.6;
        ctx.beginPath();
        ctx.moveTo(px - bw * 0.25, py + bh);
        ctx.lineTo(px + bw * (n2 - 0.5), py + bh * 1.3);
        ctx.stroke();
      }

      if (t === Tile.Rift) {
        const glow = ctx.createRadialGradient(px, py + bh, 0, px, py + bh, bw * 1.2);
        glow.addColorStop(0, 'rgba(160,110,255,0.45)');
        glow.addColorStop(0.45, 'rgba(90,50,160,0.22)');
        glow.addColorStop(1, 'rgba(40,20,70,0)');
        ctx.fillStyle = glow;
        diamond(ctx, px, py, bw, bh);
        ctx.fill();
      }

      if (t === Tile.Ore) {
        ctx.fillStyle = `rgba(220,170,60,${0.12 + n * 0.18})`;
        diamond(ctx, px, py, bw * 0.72, bh * 0.72);
        ctx.fill();
      }
    }
  }

  const blit = (stamp: Stamp, tx: number, ty: number, scale = 1): void => {
    const px = (tx - ty) * bw + OX;
    const py = (tx + ty) * bh + bh;
    const dw = stamp.canvas.width * scale;
    const dh = stamp.canvas.height * scale;
    ctx.drawImage(stamp.canvas, px - stamp.gx * scale, py - stamp.gy * scale, dw, dh);
  };

  // 体积岩石、矿晶、裂隙晶柱、废墟（必须按 iso 深度顺序盖上去）
  for (let sum = 0; sum < MAP_W + MAP_H; sum++) {
    for (let tx = 0; tx < MAP_W; tx++) {
      const ty = sum - tx;
      if (ty < 0 || ty >= MAP_H) continue;
      const i = ty * MAP_W + tx;
      const t = tiles[i]!;
      const h = hash(tx * 3, ty * 7);

      if (t === Tile.Rock) {
        const arr = stamps.rock!;
        blit(arr[(h * arr.length) | 0]!, tx, ty, 0.85 + h * 0.35);
        continue;
      }
      if (t === Tile.Ore) {
        if (h > 0.22) {
          const arr = stamps.ore!;
          blit(arr[(h * arr.length) | 0]!, tx, ty, 0.75 + h * 0.3);
        }
        continue;
      }
      if (t === Tile.Rift) {
        if (h > 0.55) blit(stamps.spire![(h * stamps.spire!.length) | 0]!, tx, ty, 0.8 + h * 0.4);
        continue;
      }

      if (nearBase(tx, ty, bases, 8)) continue;
      if (t !== Tile.Ground) continue;

      if (h > 0.965) blit(stamps.ruin![(h * 40) % stamps.ruin!.length | 0]!, tx, ty, 0.9);
      else if (h > 0.93) blit(stamps.barrel![(h * 30) % stamps.barrel!.length | 0]!, tx, ty, 0.85);
      else if (h > 0.88) blit(stamps.scrap![(h * 50) % stamps.scrap!.length | 0]!, tx, ty, 0.8 + (h - 0.88) * 2);
    }
  }

  // 裂隙中心额外光晕（世界地标）
  for (const r of rifts) {
    const px = (r.x - r.y) * bw + OX;
    const py = (r.x + r.y) * bh + bh;
    const g = ctx.createRadialGradient(px, py, 0, px, py, bw * 7);
    g.addColorStop(0, 'rgba(190,150,255,0.35)');
    g.addColorStop(0.35, 'rgba(110,70,190,0.16)');
    g.addColorStop(1, 'rgba(40,20,80,0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.ellipse(px, py, bw * 7, bh * 7, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  // 小地图
  const mm = document.createElement('canvas');
  mm.width = MAP_W;
  mm.height = MAP_H;
  const mctx = mm.getContext('2d')!;
  const mimg = mctx.createImageData(MAP_W, MAP_H);
  for (let i = 0; i < MAP_W * MAP_H; i++) {
    const t = tiles[i]!;
    const o = i * 4;
    let col: [number, number, number];
    if (t === Tile.Rock) col = [102, 94, 82];
    else if (t === Tile.Ore) col = [198, 148, 52];
    else if (t === Tile.Rift) col = [92, 72, 140];
    else col = [72, 80, 62];
    mimg.data[o] = col[0];
    mimg.data[o + 1] = col[1];
    mimg.data[o + 2] = col[2];
    mimg.data[o + 3] = 255;
  }
  mctx.putImageData(mimg, 0, 0);

  return { canvas: c, minimap: mm, originX: OX / S, scale: S };
}
